//HERTable.jsx
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TablePagination,
  Paper,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import { Visibility, Download } from "@mui/icons-material";
import HERPreviewModal from "./actions/HERPreviewModal";
import { BglLoading } from "components/loading/BglLoading";
import { HER_ACTIONS } from "store/actions/herActions";
import { toast } from "react-toastify";
import "./HERStyles.scss";

const HERTable = ({
  notifications,
  selectedNotifications,
  setSelectedNotifications,
  currentPage,
  totalNotifications,
  notificationsPerPage,
  onPageChange,
  setNotificationsPerPage,
  setCurrentPage,
  endpointUrl,
  token,
  handleFetchNotifications,
  isLoading,
  error,
}) => {
  const dispatch = useDispatch();
  const { notificationDetails }= useSelector((state) => state.her);

  const [previewContent, setPreviewContent] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [notificationFocus, setNotificationFocus] = useState(null);
  const [focusIndex, setFocusIndex] = useState(null);
  const [previewButtonsDisabled, setPreviewButtonsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [countdownTimer, setCountdownTimer] = useState(null);

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [countdownTimer]);
  const [loadingPreview, setLoadingPreview] = useState(null); // Track which notification is loading
  const [disabledButtons, setDisabledButtons] = useState(new Set()); // Track disabled buttons

  const handleSelectNotification = (notification) => {
    setSelectedNotifications((prev) =>
      prev.includes(notification)
        ? prev.filter((item) => item !== notification)
        : [...prev, notification]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedNotifications(notifications);
    } else {
      setSelectedNotifications([]);
    }
  };

  const handlePreview = async (notification) => {
    const notificationId = notification.notificationId;

    // Check if notification is already cached
    const cachedNotification = notificationDetails[notificationId];

    if (cachedNotification) {
      // Use cached data - no countdown needed
      console.log("Using cached notification data for:", notificationId);
      setPreviewContent(cachedNotification);
      setNotificationFocus(notification);
      const index = notifications.findIndex(noti => noti.notificationId === notificationId);
      setFocusIndex(index);
      setOpenPreview(true);
      return;
    }

    // Not cached, fetch from API
    if (previewButtonsDisabled) return;

    // Disable all preview buttons immediately
    setPreviewButtonsDisabled(true);
    setCountdown(10);

    // Start countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCountdownTimer(null);
          setPreviewButtonsDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    setCountdownTimer(timer);

    try {
      const url = `${endpointUrl}/2.1/utility_notifications/notifications/${notificationId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Extract only the fields we're interested in from the payload
      const notificationDetail = {
        notificationTitle: data.payload?.notificationTitle || '',
        notificationText: data.payload?.notificationText || '',
        notificationBody: data.payload?.notificationBody || '',
        metaData: data.payload?.metaData || {},
      };

      // Store in Redux cache
      dispatch(HER_ACTIONS.SET_NOTIFICATION_DETAIL(notificationId, notificationDetail));

      setPreviewContent(notificationDetail);
      setNotificationFocus(notification);
      const index = notifications.findIndex(noti => noti.notificationId === notificationId);
      setFocusIndex(index);
      setOpenPreview(true);
    } catch (error) {
      console.error("Error fetching HER preview:", error);
      toast.error(`Failed to fetch HER preview: ${error.message}`);
      // If there's an error, re-enable buttons immediately
      clearInterval(timer);
      setCountdownTimer(null);
      setPreviewButtonsDisabled(false);
      setCountdown(0);
    } finally {
      setCountdown(0);
    }
  };

  const handleNotificationChangeFromPreview = async (newIndex) => {
    if (newIndex >= 0 && newIndex < notifications.length) {
      const newNotification = notifications[newIndex];
      await handlePreview(newNotification);
    }
  };

  const handlePageChange = (_event, newPage) => {
    setCurrentPage(newPage);
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setNotificationsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'generated':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Paper style={{ marginTop: "16px", padding: "16px" }}>
      {isLoading ? (
        <BglLoading />
      ) : error ? (
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      ) : notifications && notifications.length > 0 ? (
        <>
          <Typography variant="body1" style={{ marginBottom: "16px" }}>
            Total HER Notifications: {totalNotifications}
          </Typography>
          <TableContainer component={Paper} className="her-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedNotifications.length > 0 &&
                    selectedNotifications.length < notifications.length
                  }
                  checked={
                    notifications.length > 0 &&
                    selectedNotifications.length === notifications.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Notification ID</TableCell>
              <TableCell>Delivery Destination</TableCell>
              <TableCell>Notification Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Generation Time</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications && notifications.map((notification, index) => (
              <TableRow key={notification.notificationId || index}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedNotifications.includes(notification)}
                    onChange={() => handleSelectNotification(notification)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" className="notification-id">
                    {notification.notificationId}
                  </Typography>
                </TableCell>
                <TableCell>{notification.deliveryDestination || "N/A"}</TableCell>
                <TableCell>{notification.notificationTitle || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    label={notification.status || "Unknown"}
                    color={getStatusColor(notification.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(notification.generationTimestamp)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => handlePreview(notification)}
                      disabled={previewButtonsDisabled}
                    >
                      {previewButtonsDisabled ? `Wait ${countdown}s` : "Preview"}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

          <TablePagination
            component="div"
            count={totalNotifications}
            page={currentPage}
            onPageChange={handlePageChange}
            rowsPerPage={notificationsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </>
      ) : (
        <Typography variant="body2" color="textSecondary" align="center">
          No notifications found
        </Typography>
      )}

      {/* HER Preview Modal */}
      {openPreview && (
        <HERPreviewModal
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          content={previewContent}
          notification={notificationFocus}
          notificationsPerPage={notificationsPerPage}
          focusIndex={focusIndex}
          changePreviewNotification={handleNotificationChangeFromPreview}
          totalNotifications={totalNotifications}
        />
      )}
    </Paper>
  );
};

export default HERTable;
